import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaShield, FaLock, FaBook, FaBell, FaCircleCheck, FaUser } from 'react-icons/fa6';

/**
 * MINERVA REFACTORED - Política de Privacidade LGPD
 * Premium Design v3.0 | Chumbo + Dourado
 * Paleta de Marca | Efeito Sutil Luxuoso
 * Last Updated: 2026-01-28
 */

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string | string[];
  highlight?: string;
}

const LGPD: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>('coleta');
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-scroll-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const sections: Section[] = [
    {
      id: 'coleta',
      title: 'Quais dados coletamos?',
      icon: <FaBook />,
      content: 'Coletamos os seguintes dados pessoais para fins institucionais:',
      highlight: 'Nome, CPF, RG, endereço, telefone, e-mail, foto, dados profissionais e documentos comprobatórios.',
    },
    {
      id: 'utilizacao',
      title: 'Para que utilizamos esses dados?',
      icon: <FaCircleCheck />,
      content: [
        'Cadastro e filiação',
        'Emissão de carteirinha, diplomas e certificados',
        'Comunicação institucional',
        'Encaminhamento a parceiros educacionais',
        'Cumprimento de obrigações legais',
      ],
    },
    {
      id: 'compartilhamento',
      title: 'Com quem compartilhamos seus dados?',
      icon: <FaUser />,
      content: [
        'Somente com instituições parceiras autorizadas',
        'Autoridades públicas, quando exigido por lei',
        'Prestadores de serviços sob cláusula de confidencialidade',
      ],
    },
    {
      id: 'armazenamento',
      title: 'Por quanto tempo armazenamos seus dados?',
      icon: <FaLock />,
      content: 'Durante o período de filiação ou conforme exigência legal.',
      highlight: 'Sem prazos indefinidos - total conformidade com LGPD',
    },
    {
      id: 'direitos',
      title: 'Quais são seus direitos?',
      icon: <FaBell />,
      content: [
        'Solicitar acesso aos seus dados pessoais',
        'Requerer correção de dados incorretos',
        'Solicitar exclusão dos dados (direito ao esquecimento)',
        'Revogar consentimento a qualquer momento',
        'Receber cópia dos dados em formato portável',
      ],
    },
    {
      id: 'seguranca',
      title: 'Segurança da Informação',
      icon: <FaShield />,
      content: 'Adotamos medidas rigorosas para proteger seus dados contra acessos não autorizados, vazamentos ou perdas.',
      highlight: 'Criptografia, firewalls, controle de acesso e monitoramento contínuo',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        [data-scroll-animate] {
          opacity: 0;
          animation: fadeInUp 0.7s ease-out forwards;
        }

        .scroll-visible {
          opacity: 1 !important;
        }

        [data-delay="1"] { animation-delay: 0.1s; }
        [data-delay="2"] { animation-delay: 0.2s; }
        [data-delay="3"] { animation-delay: 0.3s; }
        [data-delay="4"] { animation-delay: 0.4s; }
        [data-delay="5"] { animation-delay: 0.5s; }
        [data-delay="6"] { animation-delay: 0.6s; }

        .section-header {
          position: relative;
          overflow: hidden;
        }

        .section-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(180, 83, 9, 0.04) 100%);
          pointer-events: none;
        }

        .section-header::after {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(217, 119, 6, 0.1) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          pointer-events: none;
        }

        .list-item {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-20 pb-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              id="hero-title"
              data-scroll-animate
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight scroll-visible drop-shadow-lg"
            >
              Política de Privacidade
            </h1>
            <p 
              id="hero-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg md:text-xl text-gray-100 mb-8 leading-relaxed scroll-visible drop-shadow-md"
            >
              A Federação Nacional dos Profissionais da Beleza, comprometida com a <span className="font-black text-amber-300">transparência e o respeito à privacidade</span> de seus Filiados e parceiros, estabelece esta Política de Privacidade em conformidade com a <span className="font-black text-amber-300">Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018)</span>.
            </p>

            <div className="flex justify-center gap-4 flex-wrap">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-amber-500/30 flex items-center gap-2">
                <FaShield className="w-5 h-5 text-amber-300" />
                <span className="font-black text-sm">100% Seguro</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-amber-500/30 flex items-center gap-2">
                <FaLock className="w-5 h-5 text-amber-300" />
                <span className="font-black text-sm">Criptografado</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-amber-500/30 flex items-center gap-2">
                <FaCircleCheck className="w-5 h-5 text-amber-300" />
                <span className="font-black text-sm">Conforme LGPD</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEÇÕES EXPANSÍVEIS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 
              id="sections-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Detalhes da Nossa Política
            </h2>
            <p 
              id="sections-desc"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-12 max-w-2xl mx-auto scroll-visible"
            >
              Clique em cada seção para conhecer todos os detalhes sobre como protegemos seus dados
            </p>

            <div className="space-y-4">
              {sections.map((section, idx) => (
                <div 
                  key={section.id}
                  id={`section-${section.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="bg-white rounded-xl overflow-hidden border-2 border-amber-200/60 shadow-lg scroll-visible"
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                    className="section-header w-full bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white flex items-center justify-between hover:opacity-95 transition-all relative"
                  >
                    <div className="flex items-center gap-4 relative z-10">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-lg flex items-center justify-center border border-amber-500/30">
                        <span className="text-amber-400 text-xl">{section.icon}</span>
                      </div>
                      <h3 className="text-lg md:text-xl font-black text-left">{section.title}</h3>
                    </div>
                    <FaChevronDown
                      className={`w-5 h-5 transition-transform flex-shrink-0 relative z-10 ${expandedSection === section.id ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Content */}
                  {expandedSection === section.id && (
                    <div className="p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white">
                      {typeof section.content === 'string' ? (
                        <div>
                          <p className="text-gray-700 mb-4 leading-relaxed text-lg">{section.content}</p>
                          {section.highlight && (
                            <div className="mt-4 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                              <p className="text-gray-800 font-semibold">
                                <span className="text-amber-600 font-black">✓ </span>
                                {section.highlight}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <ul className="space-y-3">
                          {section.content.map((item, itemIdx) => (
                            <li 
                              key={itemIdx} 
                              className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-amber-300/50 transition-colors list-item"
                              style={{
                                animation: `fadeInUp 0.5s ease-out ${itemIdx * 50}ms both`,
                              }}
                            >
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                                <span className="text-white text-xs font-black">✓</span>
                              </div>
                              <span className="text-gray-700 font-medium leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ALTERAÇÕES E ATUALIZAÇÕES */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <div 
              id="updates-card"
              data-scroll-animate
              className="bg-white rounded-2xl p-8 md:p-10 border-2 border-amber-200 shadow-lg scroll-visible"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-lg flex items-center justify-center border border-amber-500/30 flex-shrink-0">
                  <FaBell className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Alterações Nesta Política</h3>
                  <p className="text-gray-600">
                    Esta Política poderá ser atualizada conforme mudanças na legislação ou em nossos processos internos. Recomendamos que você a revise periodicamente para estar sempre informado sobre como protegemos seus dados.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200/60">
                <p className="text-sm text-gray-700">
                  <span className="font-black text-amber-600">Última atualização: </span>
                  Janeiro de 2026
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTATO E SUPORTE */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              id="contact-title"
              data-scroll-animate
              className="text-3xl font-black mb-6 scroll-visible"
            >
              Dúvidas sobre Privacidade?
            </h2>
            <p 
              id="contact-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg text-gray-100 mb-8 leading-relaxed scroll-visible"
            >
              Se você tiver qualquer dúvida sobre esta Política de Privacidade ou sobre como protegemos seus dados, entre em contato conosco imediatamente. Estamos aqui para ajudar e garantir total transparência.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div 
                id="email-contact"
                data-scroll-animate
                data-delay="2"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-amber-500/30 scroll-visible hover:bg-white/15 transition-all"
              >
                <p className="text-sm text-gray-300 mb-2">E-mail</p>
                <p className="text-xl font-black text-amber-300">privacidade@federacao.org.br</p>
              </div>

              <div 
                id="phone-contact"
                data-scroll-animate
                data-delay="3"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-amber-500/30 scroll-visible hover:bg-white/15 transition-all"
              >
                <p className="text-sm text-gray-300 mb-2">Telefone</p>
                <p className="text-xl font-black text-amber-300">+55 (85) 3301-5000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div 
            id="cta-final"
            data-scroll-animate
            className="max-w-2xl mx-auto text-center bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 md:p-12 border-2 border-amber-300 shadow-lg scroll-visible"
          >
            <h3 className="text-2xl font-black text-gray-900 mb-4">
              Seus Dados, Sua Privacidade, Nossa Responsabilidade
            </h3>
            <p className="text-gray-700 mb-8 leading-relaxed">
              Proteger seus dados pessoais é uma prioridade máxima para a Federação. Concordar com esta política significa que você confia em nosso compromisso com a segurança e a transparência.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#"
                className="px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-black rounded-lg hover:shadow-lg transition-all"
              >
                Concordo com a Política
              </a>
              <a
                href="#"
                className="px-8 py-3 bg-white text-gray-900 font-black rounded-lg border-2 border-amber-300 hover:bg-gray-50 transition-all"
              >
                Solicitar Informações
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LGPD;